import { TreeItemProps } from "@mui/x-tree-view/TreeItem";
import { useTreeItem } from "@mui/x-tree-view-pro";
import React, { forwardRef, Ref, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RateBuilderRowContent } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateBuilderRowContent";
import { RateBuilderContextMenu } from "@sinch/components/application/RatelistBuilder/RateTreeItem/RateBuilderContextMenu";
import { RateBuilderItemToolsBar } from "@sinch/components/application/RatelistBuilder/RateTreeItem/RateBuilderItemToolsBar";
import { RateBuilderTreeItem } from "@sinch/components/application/RatelistBuilder/RateTreeItem/RateBuilderTreeItem";
import { RateBuilderTreeViewItem } from "@sinch/components/application/RatelistBuilder/types";
import { useRateBuilderSettings } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";
import { findItemInTree } from "@sinch/components/application/RatelistBuilder/utils/treeUtils";
import { useContextMenu } from "@sinch/components/hooks/UseContextMenu";
import { RateBuilderRowOverview } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateBuilderRowOverview/RateBuilderRowOverview";

export const RateBuilderItem = forwardRef((props: TreeItemProps, ref: Ref<HTMLLIElement>) => {
  const { t } = useTranslation();
  const { publicAPI } = useTreeItem({ ...props, rootRef: ref });
  const { disableNesting, mode } = useRateBuilderSettings();

  const item = findItemInTree<RateBuilderTreeViewItem>(publicAPI.getItemTree(), props.itemId);

  const content = useMemo(
    () =>
      mode === "view" || mode === "override" ? (
        <RateBuilderRowOverview rowId={props.itemId} />
      ) : (
        <RateBuilderRowContent rowId={props.itemId} />
      ),
    [props.itemId, mode]
  );

  const { onContextMenu, menuProps, onClose } = useContextMenu();

  const rowId = props.itemId;

  if (!item) {
    throw new Error("Item not found");
  }

  return (
    <>
      <RateBuilderTreeItem
        ref={ref}
        bottomNestedToolsBar={
          disableNesting ? undefined : (
            <RateBuilderItemToolsBar
              data-cy="RateBuilder-add-nested"
              label={t("RateBuilder.addNestedRate")}
              parentId={rowId}
            />
          )
        }
        bottomSiblingToolsBar={
          disableNesting ? undefined : <RateBuilderItemToolsBar label={t("RateBuilder.addRate")} parentId={rowId} />
        }
        content={content}
        onContextMenu={onContextMenu}
        {...props}
      />
      {mode === "edit" && <RateBuilderContextMenu {...menuProps} onClose={onClose} rowId={rowId} />}
    </>
  );
});
RateBuilderItem.displayName = "RateBuilderItem";
